import { List, Map } from 'immutable'
import { selectResolver } from 'services/resolver/selectors'
import {
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
  SUBTYPE_CONTENT_YOGA,
  SUBTYPE_CONTENT_FITNESS,
  SUBTYPE_CONTENT_MEDITATION,
} from 'services/content-type'
import {
  EPISODE_TAB_VALUE, EVENT_DISPLAY_TYPE, RELATED_TAB_VALUE, SERIES_TAB_VALUE, VIDEO_TAB_VALUE,
} from '.'

/**
 * @param {ReduxState} state redux state
 * @returns {Map}
 */
export const selectDetail = (state) => state.detail || Map()

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export const selectIsDetailProcessing = (state) => selectDetail(state).get('processing', false)

/**
 * @param {ReduxState} state redux state
 * @returns {Map}
 */
export const selectDetailData = (state) => selectDetail(state).get('data', Map())

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export const selectIsDetailDataEmpty = (state) => selectDetail(state).get('data', Map()).size === 0

/**
 * @param {ReduxState} state redux state
 * @returns {List}
 */
export const selectDetailFileAttachments = (state) => selectDetail(state).getIn(['data', 'fileAttachments'], List())

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export const selectDetailContenType = (state) => selectDetailData(state).get('contentType', '')

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export const selectDetailLanguage = (state) => selectDetailData(state).get('contentLanguage')

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export const selectIsEpisode = (state) => {
  const contentType = selectDetailContenType(state)
  const seriesId = selectDetailData(state).get('seriesId')

  return contentType === TYPE_CONTENT_VIDEO && seriesId > 0
}

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export const selectIsSeries = (state) => {
  const contentType = selectDetailContenType(state)

  return contentType === TYPE_CONTENT_SERIES
}

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export const selectIsVideo = (state) => {
  const contentType = selectDetailContenType(state)
  const seriesId = selectDetailData(state).get('seriesId')

  return contentType === TYPE_CONTENT_VIDEO && seriesId <= 0
}

/**
 * @param {ReduxState} state redux state
 * @returns {Map}
 */
export const selectFeaturedEpisode = (state) => {
  const isSeriesNode = selectIsSeries(state)

  let featuredEpisode = Map()

  if (isSeriesNode) {
    const featuredEpisodes = selectDetailData(state).get('featuredEpisode', List())

    if (featuredEpisodes) {
      featuredEpisode = featuredEpisodes.first()
    }
  } else {
    featuredEpisode = selectDetailData(state)
  }

  return featuredEpisode
}

/**
 * @param {ReduxState} state redux state
 * @returns {Map}
 */
export const selectSeriesDetailData = (state) => selectDetailData(state).get('seriesDetail', Map())

/**
 * Select season nums list.
 * Different fields because of node type. Episode detail data always has empty seasonNums
 * @param {ReduxState} state redux state
 * @returns {List}
 */
export const selectSeasonNumbers = (state) => {
  const isEpisodeNode = selectIsEpisode(state)

  if (isEpisodeNode) {
    return selectSeriesDetailData(state).get('seasonNums', List())
  }

  return selectDetailData(state).get('seasonNums', List())
}

/**
 * Select current season number.
 * @param {ReduxState} state redux state
 * @returns {Number}
 */
export const selectCurrentSeasonNumber = (state) => selectFeaturedEpisode(state).get('season', 1)

export const selectSeriesId = (state) => {
  const isEpisodeNode = selectIsEpisode(state)

  if (isEpisodeNode) {
    return selectSeriesDetailData(state).get('id', -1)
  }

  return selectDetailData(state).get('id', -1)
}

/**
 * @param {ReduxState} state redux state
 * @returns {Map}
 */
export const selectTabData = (activeTabValue) => (state) => {
  const detailData = selectDetailData(state)
  const featuredEpisode = selectFeaturedEpisode(state)
  const seriesDetailData = selectSeriesDetailData(state)
  const isCurrentNodeSeries = selectIsSeries(state)
  const isCurrentNodeEpisode = selectIsEpisode(state)

  if (activeTabValue === VIDEO_TAB_VALUE
    || (isCurrentNodeSeries && activeTabValue === SERIES_TAB_VALUE)
    || (isCurrentNodeEpisode && activeTabValue === EPISODE_TAB_VALUE)
  ) {
    return detailData
  }

  if (
    (isCurrentNodeSeries && activeTabValue === EPISODE_TAB_VALUE)
    || (isCurrentNodeSeries && activeTabValue === RELATED_TAB_VALUE)
  ) {
    return featuredEpisode
  }

  if (isCurrentNodeEpisode && activeTabValue === SERIES_TAB_VALUE) {
    return seriesDetailData
  }

  return detailData
}

/**
 * @param {ReduxState} state redux state
 * @returns {List}
 */
export const selectRelatedContentDetailData = (state) => selectDetailData(state).get('relatedContent', List())

/**
 * @param {ReduxState} state redux state
 * @returns {List}
 */
export const selectDeatilRelatedVideo = (state) => selectDetailData(state).get('relatedVideo', List())

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export const selectIsPlaceholderExist = (state) => selectDetail(state).get('placeholderExists', false)

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export const selectIsEvent = (state) => {
  const isEpisodeNode = selectIsEpisode(state)

  if (isEpisodeNode) {
    return selectSeriesDetailData(state).get('displayType') === EVENT_DISPLAY_TYPE
  }

  return selectDetailData(state).get('displayType') === EVENT_DISPLAY_TYPE
}

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export const selectIsDetailSync = (state) => {
  const detail = selectDetail(state)
  const resolver = selectResolver(state)

  const resolvedId = resolver.getIn(['data', 'id'])

  return (
    resolvedId === detail.get('id')
    && resolvedId === detail.getIn(['data', 'id'])
  )
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export const selectSeriesContentSubtype = (state) => {
  return selectSeriesDetailData(state).get('contentSubtype') || ''
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export const selectDetailContentSubtype = (state) => {
  return selectDetailData(state).get('contentSubtype') || ''
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export const selectIsYogaContentSubtype = (state) => {
  const contentSubtype = selectDetailContentSubtype(state)

  return contentSubtype === SUBTYPE_CONTENT_YOGA
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export const selectIsFitnessContentSubtype = (state) => {
  const contentSubtype = selectDetailContentSubtype(state)

  return contentSubtype === SUBTYPE_CONTENT_FITNESS
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export const selectIsMeditationContentSubtype = (state) => {
  const contentSubtype = selectDetailContentSubtype(state)

  return contentSubtype === SUBTYPE_CONTENT_MEDITATION
}
